import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ProduceCard from '@/components/ProduceCard';
import RequirementCard from '@/components/RequirementCard';
import StarRating from '@/components/StarRating';
import LikeFarmerButton from '@/components/LikeFarmerButton';
import BottomNav from '@/components/BottomNav';
import Avatar from '@/components/Avatar';
import { ArrowLeft, MapPin, Shield, ChevronRight, MessageSquare, Users, Sprout, ShoppingBag, Layers } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Unified profile page — same layout for the owner, admin, and any viewer.
 * Reachable at both `/farmer/:farmerId` (legacy) and `/u/:userId`.
 */
export default function FarmerProfilePage() {
  const params = useParams();
  const userId = params.farmerId || params.userId;
  const navigate = useNavigate();
  const { api, user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [produce, setProduce] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [reviewSummary, setReviewSummary] = useState({ avg_rating: 0, review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'produce' | 'requirements'

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/farmer/${userId}/reviews`);
      setReviewSummary({
        avg_rating: data.avg_rating || 0,
        review_count: data.review_count || 0,
      });
    } catch { /* no reviews yet */ }
  }, [api, userId]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [profileRes, produceRes, reqRes, savedRes] = await Promise.allSettled([
          api.get(`/farmer/${userId}/profile`),
          api.get(`/farmer/${userId}/produce`),
          api.get(`/user/${userId}/requirements`),
          api.get('/saved/ids'),
        ]);
        if (profileRes.status !== 'fulfilled') throw new Error('not found');
        setProfile(profileRes.value.data);
        setProduce(produceRes.status === 'fulfilled' ? (produceRes.value.data.posts || []) : []);
        setRequirements(reqRes.status === 'fulfilled' ? (reqRes.value.data.posts || []) : []);
        setSavedIds(new Set(savedRes.status === 'fulfilled' ? (savedRes.value.data.post_ids || []) : []));
        await loadSummary();
      } catch {
        toast.error('Profile not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, userId, navigate, loadSummary]);

  const handleToggleSave = async (postId) => {
    try {
      const { data } = await api.post(`/saved/${postId}`);
      setSavedIds(prev => {
        const next = new Set(prev);
        if (data.saved) next.add(postId); else next.delete(postId);
        return next;
      });
    } catch { /* ignore */ }
  };

  const isFarmer = profile?.role === 'farmer';
  const isAdminUser = profile?.role === 'admin';
  const roleLabel = isFarmer ? 'Verified Farmer' : isAdminUser ? 'Frootex Admin' : 'Buyer';

  // Merge for the "All" view, sorted by created_at desc
  const merged = useMemo(() => {
    const all = [
      ...produce.map(p => ({ ...p, __type: 'produce' })),
      ...requirements.map(r => ({ ...r, __type: 'requirement' })),
    ];
    all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return all;
  }, [produce, requirements]);

  const filteredList = filter === 'produce'
    ? produce.map(p => ({ ...p, __type: 'produce' }))
    : filter === 'requirements'
      ? requirements.map(r => ({ ...r, __type: 'requirement' }))
      : merged;

  const emptyCopy = filter === 'requirements'
    ? 'No requirements posted yet'
    : filter === 'produce'
      ? 'No produce available'
      : 'No posts yet';

  const filterPills = [
    { value: 'all',          label: 'All',        count: merged.length,       icon: Layers,      testId: 'profile-filter-all' },
    { value: 'produce',      label: 'Produce',    count: produce.length,      icon: Sprout,      testId: 'profile-filter-produce' },
    { value: 'requirements', label: 'Requests',   count: requirements.length, icon: ShoppingBag, testId: 'profile-filter-requirements' },
  ];

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF] p-4">
        <div className="animate-pulse space-y-4">
          <div className="w-20 h-20 rounded-full bg-[#EFF2F7] mx-auto" />
          <div className="h-5 bg-[#EFF2F7] rounded w-1/3 mx-auto" />
          <div className="h-48 bg-[#EFF2F7] rounded-xl" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const isOwnerView = me?._id === userId;
  const title = isOwnerView ? 'My Profile' : isFarmer ? 'Farmer Profile' : 'Profile';

  return (
    <div
      className="w-full max-w-md mx-auto min-h-screen bg-[#F6F8FB] md:max-w-xl lg:max-w-2xl border-x border-[#E4E8EF]"
      data-testid="farmer-profile-page"
    >
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#F6F8FB]/80 backdrop-blur-xl border-b border-[#E4E8EF] px-4 py-3 flex items-center gap-3">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
        </button>
        <h1 className="text-lg font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">{title}</h1>
      </div>

      <div className="p-4 pb-24 space-y-5">
        {/* Profile Card */}
        <div className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-5 text-center animate-fade-in-up">
          <Avatar name={profile.name} src={profile.avatar} size={80} className="mx-auto border-2 border-[#E4E8EF]" />
          <h2 className="text-xl font-bold text-[#0B1620] mt-3 font-['Bricolage_Grotesque']">{profile.name}</h2>
          <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
            <span
              data-testid="profile-role-badge"
              className={`px-2 py-0.5 text-xs font-bold rounded-md flex items-center gap-1 ${
                isFarmer
                  ? 'bg-[#15803D]/10 text-[#15803D]'
                  : isAdminUser
                    ? 'bg-[#0B4F3F]/10 text-[#0B4F3F]'
                    : 'bg-[#EFF2F7] text-[#5B6675]'
              }`}
            >
              <Shield className="w-3 h-3" /> {roleLabel}
            </span>
            {isFarmer && typeof profile.follower_count === 'number' && profile.follower_count > 0 && (
              <span
                data-testid="follower-count"
                className="px-2 py-0.5 bg-[#EFF2F7] text-[#5B6675] text-xs font-bold rounded-md flex items-center gap-1"
              >
                <Users className="w-3 h-3" />
                {profile.follower_count} {profile.follower_count === 1 ? 'follower' : 'followers'}
              </span>
            )}
          </div>
          {profile.location && (
            <p className="text-sm text-[#5B6675] mt-2 flex items-center justify-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {profile.location}
            </p>
          )}

          {/* Like / Unlike — only visible to non-owner viewers of farmers */}
          {isFarmer && !isOwnerView && (
            <div className="mt-4 flex justify-center">
              <LikeFarmerButton
                farmerId={userId}
                initialFollowed={!!profile.is_followed}
                variant="pill"
                testId="profile-like-farmer-btn"
                onChange={(followed) => {
                  setProfile((prev) => prev ? {
                    ...prev,
                    is_followed: followed,
                    follower_count: Math.max(0, (prev.follower_count || 0) + (followed ? 1 : -1)),
                  } : prev);
                }}
              />
            </div>
          )}

          {/* Stats — Produce · Requests (for farmers) OR Requests · Reviews (for others) */}
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#E4E8EF]">
            {isFarmer ? (
              <>
                <div className="text-center" data-testid="stat-produce">
                  <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{produce.length}</p>
                  <p className="text-[10px] text-[#5B6675] uppercase tracking-wide">Produce</p>
                </div>
                <div className="text-center" data-testid="stat-requests">
                  <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{requirements.length}</p>
                  <p className="text-[10px] text-[#5B6675] uppercase tracking-wide">Requests</p>
                </div>
              </>
            ) : (
              <>
                <div className="text-center" data-testid="stat-requests">
                  <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{requirements.length}</p>
                  <p className="text-[10px] text-[#5B6675] uppercase tracking-wide">Requests</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{produce.length}</p>
                  <p className="text-[10px] text-[#5B6675] uppercase tracking-wide">Produce</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Reviews link — farmers only */}
        {isFarmer && (
          <button
            type="button"
            data-testid="view-reviews-btn"
            onClick={() => navigate(`/farmer/${userId}/reviews`)}
            className="w-full bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] px-4 py-3 flex items-center gap-3 active:scale-[0.99] transition-transform"
          >
            <div className="w-9 h-9 rounded-lg bg-[#EFF2F7] flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-[#0B4F3F]" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-[#0B1620]">Ratings & Reviews</p>
              <p className="text-[11px] text-[#5B6675]">
                {reviewSummary.review_count > 0
                  ? `${reviewSummary.avg_rating.toFixed(1)} ★ from ${reviewSummary.review_count} ${reviewSummary.review_count === 1 ? 'review' : 'reviews'} · Tap to read all`
                  : 'No reviews yet · Tap to leave one'}
              </p>
              <div className="mt-1"><StarRating value={reviewSummary.avg_rating} size={12} /></div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#5B6675]" />
          </button>
        )}

        {/* Filter pills */}
        <div
          role="tablist"
          data-testid="profile-filter-pills"
          className="grid grid-cols-3 gap-1 p-1 bg-[#EFF2F7] rounded-full border border-[#E4E8EF]"
        >
          {filterPills.map((p) => {
            const Icon = p.icon;
            const active = filter === p.value;
            return (
              <button
                key={p.value}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={p.testId}
                onClick={() => setFilter(p.value)}
                className={`relative z-10 h-10 rounded-full flex items-center justify-center gap-1 text-[11px] font-semibold transition-all duration-200 ${
                  active
                    ? 'bg-[#F6F8FB] text-[#0B1620] shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]'
                    : 'text-[#5B6675] hover:text-[#0B1620]'
                }`}
              >
                <Icon className="w-3 h-3" />
                {p.label}
                <span className={`text-[10px] tabular-nums ${active ? 'opacity-80' : 'opacity-60'}`}>
                  {p.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filtered chronological feed */}
        <div data-testid="profile-posts-feed">
          {filteredList.length === 0 ? (
            <div className="text-center py-10 bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF]">
              <p className="text-sm text-[#5B6675]">{emptyCopy}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredList.map((post, i) => (
                post.__type === 'requirement' ? (
                  <RequirementCard
                    key={`req-${post.id}`}
                    post={post}
                    index={i}
                  />
                ) : (
                  <ProduceCard
                    key={`prod-${post.id}`}
                    post={post}
                    index={i}
                    isSaved={savedIds.has(post.id)}
                    onToggleSave={handleToggleSave}
                  />
                )
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
